<template>
  <div class="travel-orbit" :class="{ 'is-vertical': vertical }" ref="orbit" data-image-enlarge="off">
    <div id="showcaseimages" ref="scene" class="travel-orbit-scene" aria-hidden="true"
      @pointerdown="beginDrag" @pointermove="drag" @pointerup="endDrag" @pointercancel="endDrag" @lostpointercapture="endDrag">
      <img v-for="(photo, index) in photos" :key="index" :ref="el => frames[index] = el"
        class="travel-orbit-photo" :src="photo.preview" :width="photo.width" :height="photo.height"
        alt="" draggable="false" decoding="async">
    </div>
    <div class="travel-orbit-controls" role="group" aria-label="Browse travel photos">
      <button type="button" class="is-previous" :aria-label="vertical ? 'Move travel photos up' : 'Move travel photos left'" @click="step(-1, $event)">
        <svg viewBox="0 0 64 64" fill="currentColor" aria-hidden="true"><path d="M24 3 3 32l21 29 15-12-12-17 12-17z"/></svg>
      </button>
      <button type="button" class="is-next" :aria-label="vertical ? 'Move travel photos down' : 'Move travel photos right'" @click="step(1, $event)">
        <svg viewBox="0 0 64 64" fill="currentColor" aria-hidden="true"><path d="m40 3 21 29-21 29-15-12 12-17-12-17z"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
import { travelGalleryImages, aboutGalleryPreview, aboutGallerySize } from '../data/aboutGallery';
const count = 12;
const photograph = index => {
  const name = travelGalleryImages[(index % travelGalleryImages.length + travelGalleryImages.length) % travelGalleryImages.length];
  const [width, height] = aboutGallerySize(name);
  return { preview: aboutGalleryPreview(name), width, height };
};

export default {
  name: 'TravelPhotoOrbit',
  data() { return { photos: Array.from({ length: count }, (_, i) => photograph(i * 7)), frames: [], vertical: false }; },
  mounted() {
    this._position = 0;
    this._offset = 0;
    this._target = 0;
    this._laps = Array(count).fill(null);
    this._active = true;
    this._visible = true;
    this._reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this._resize = new ResizeObserver(() => {
      const rect = this.$refs.orbit.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this._width = rect.width;
      this._height = rect.height;
      // Hysteresis keeps mobile browser chrome from repeatedly changing the axis.
      const vertical = rect.width / rect.height < (this.vertical ? .73 : .68);
      if (vertical !== this.vertical) this.releaseDrag();
      this.vertical = vertical;
      const spacing = vertical
        ? Math.max(240, Math.min(430, rect.height * .34))
        : Math.max(210, Math.min(490, rect.width * .32));
      const scale = this._spacing ? spacing / this._spacing : 1;
      this._position *= scale;
      this._offset *= scale;
      this._target *= scale;
      this._spacing = spacing;
      const style = this.$refs.orbit.style;
      style.setProperty('--orbit-photo-width', `${vertical ? Math.min(620, rect.width * .88) : spacing - 28}px`);
      style.setProperty('--orbit-photo-height', `${spacing - 24}px`);
      this.draw();
    });
    this._resize.observe(this.$refs.orbit);
    this._visibility = new IntersectionObserver(([entry]) => { this._visible = entry.isIntersecting; this.resume(); });
    this._visibility.observe(this.$refs.orbit);
    this._documentVisibility = () => this.resume();
    this._motionPreference = () => this.resume();
    document.addEventListener('visibilitychange', this._documentVisibility);
    this._reduced.addEventListener('change', this._motionPreference);
    this.resume();
  },
  activated() { this._active = true; this.resume(); },
  deactivated() { this._active = false; this.releaseDrag(); this.stop(); },
  beforeUnmount() {
    this.releaseDrag();
    this.stop();
    this._resize?.disconnect();
    this._visibility?.disconnect();
    this._reduced?.removeEventListener('change', this._motionPreference);
    document.removeEventListener('visibilitychange', this._documentVisibility);
  },
  methods: {
    stop() { cancelAnimationFrame(this._frame); this._frame = null; this._last = null; },
    resume() {
      this.stop();
      if (!this._active || !this._visible || document.hidden || !this._reduced) return;
      if (this._reduced.matches) { this._offset = this._target; this.draw(); return; }
      this._frame = requestAnimationFrame(this.tick);
    },
    tick(time) {
      const dt = this._last ? Math.min((time - this._last) / 1000, .05) : 0;
      this._last = time;
      if (!this._drag) this._position += dt * (this.vertical ? 13 : -13);
      this._offset += (this._target - this._offset) * (1 - Math.exp(-dt * 7));
      this.draw();
      if (this._active && this._visible && !document.hidden) this._frame = requestAnimationFrame(this.tick);
    },
    draw() {
      if (!this._width) return;
      const distance = this._position + this._offset;
      const span = this._spacing * count;
      this.frames.forEach((frame, index) => {
        if (!frame) return;
        const raw = (index - count / 2) * this._spacing + distance;
        const lap = Math.floor((raw + span / 2) / span);
        const along = raw - lap * span;
        const extent = this.vertical ? this._height : this._width;
        const bend = Math.min(1.5, Math.abs(along) / (extent * .7));
        // A single concave ribbon crosses the viewport. Its ends recycle offscreen.
        const x = this.vertical ? -12 + bend * bend * 24 : along;
        const y = this.vertical ? along : -35 + bend * bend * 85;
        const z = this.vertical ? -180 + Math.min(1, bend * bend) * 150 : -330 + Math.min(1, bend * bend) * 300;
        const turn = Math.max(-42, Math.min(42, along / extent * (this.vertical ? 44 : -52)));
        frame.style.transform = `translate(-50%, -50%) translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${z.toFixed(2)}px) rotate${this.vertical ? 'X' : 'Y'}(${turn.toFixed(2)}deg)`;
        const near=Math.min(1,bend*bend);
        frame.style.opacity=(.84+near*.16).toFixed(3);
        if (this._laps[index] !== null && this._laps[index] !== lap) this.photos[index] = photograph((index - lap * count) * 7);
        this._laps[index] = lap;
      });
    },
    step(direction, event) {
      this._target += direction * this._spacing;
      if (event?.detail === 0) this._offset = this._target;
      this.resume();
    },
    beginDrag(event) {
      if (event.button !== 0 || this._drag) return;
      this._drag = { id: event.pointerId, x: event.clientX, y: event.clientY, target: this._target, captured: false };
    },
    drag(event) {
      const drag = this._drag;
      if (!drag || drag.id !== event.pointerId) return;
      const along = this.vertical ? event.clientY - drag.y : event.clientX - drag.x;
      const across = this.vertical ? event.clientX - drag.x : event.clientY - drag.y;
      if (!drag.captured) {
        if (Math.abs(along) < 6 || Math.abs(along) < Math.abs(across)) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.captured = true;
      }
      this._target = drag.target + along;
      this._offset = this._target;
      // Keep one animation loop running so drag input retains its velocity.
      if (!this._frame) this.resume();
    },
    endDrag(event) {
      if (this._drag?.id !== event.pointerId) return;
      this.releaseDrag();
      this.resume();
    },
    releaseDrag() {
      const id = this._drag?.id;
      this._drag = null;
      if (id !== undefined && this.$refs.scene?.hasPointerCapture(id)) this.$refs.scene.releasePointerCapture(id);
    },
  },
};
</script>

<style scoped>
.travel-orbit { --orbit-control-angle: 0deg; position: absolute; inset: 0; z-index: 0; }
.travel-orbit-scene { position: absolute; inset: 0; perspective: 1600px; perspective-origin: 50% 50%; overflow: clip; touch-action: pan-y; cursor: var(--site-grab-cursor, grab); }
.travel-orbit-scene:active { cursor: var(--site-grabbing-cursor, grabbing); }
.travel-orbit-photo { position: absolute; top: 50%; left: 50%; height: clamp(180px, 52dvh, 520px); max-width: var(--orbit-photo-width); width: auto; object-fit: contain; padding: 0; border-radius: 0; pointer-events: none; user-select: none; backface-visibility: hidden; }
.travel-orbit-controls { position: absolute; inset: 50% 24px auto; height: 0; display: flex; align-items: center; justify-content: space-between; pointer-events: none; }
.travel-orbit-controls button { position: relative; isolation: isolate; display: grid; grid-template-columns: minmax(0, 1fr); place-items: center; width: clamp(74px,10vw,136px); height: 144px; padding: 0; border: 0; border-radius: 0; background: transparent; color: rgba(255,255,255,.64); pointer-events: auto; cursor: var(--site-interactive-cursor,pointer); transition: opacity 200ms ease; }
.travel-orbit-controls button::before { position: absolute; width: 104px; height: 104px; z-index: -1; background: rgba(245,249,250,.34); backdrop-filter: blur(10px) saturate(1.45); -webkit-backdrop-filter: blur(10px) saturate(1.45); content: ''; transition: transform 260ms cubic-bezier(.22,1,.36,1); }
.travel-orbit-controls .is-previous::before { clip-path: polygon(37.5% 4.7%,4.7% 50%,37.5% 95.3%,60.9% 76.6%,42.2% 50%,60.9% 23.4%); }
.travel-orbit-controls .is-next::before { clip-path: polygon(62.5% 4.7%,95.3% 50%,62.5% 95.3%,39.1% 76.6%,57.8% 50%,39.1% 23.4%); }
.travel-orbit-controls svg { width: 104px; height: 104px; filter: drop-shadow(0 3px 6px rgba(20,26,30,.36)); transition: transform 260ms cubic-bezier(.22,1,.36,1); }
.travel-orbit-controls svg path { stroke: rgba(255,255,255,.44); stroke-width: .8; stroke-linejoin: round; }
.travel-orbit-controls .is-previous svg,.travel-orbit-controls .is-previous::before { transform: rotate(var(--orbit-control-angle)) translateX(-4px); }
.travel-orbit-controls .is-next svg,.travel-orbit-controls .is-next::before { transform: rotate(var(--orbit-control-angle)) translateX(4px); }
.travel-orbit-controls button:focus-visible { outline: 1px solid currentColor; outline-offset: -6px; }
.travel-orbit-controls button:active { scale: .96; }
.travel-orbit.is-vertical { --orbit-control-angle: 90deg; }
.is-vertical .travel-orbit-scene { touch-action: pan-x; }
.is-vertical .travel-orbit-photo { width: var(--orbit-photo-width); height: auto; max-height: var(--orbit-photo-height); object-fit: contain; }
.is-vertical .travel-orbit-controls { inset: 14px auto max(14px, env(safe-area-inset-bottom)) 50%; height: auto; width: 0; flex-direction: column; }
.is-vertical .travel-orbit-controls button { width: 104px; height: 64px; flex: 0 0 64px; }
@media (hover:hover) and (pointer:fine) {
  .travel-orbit-controls button { opacity: 0; }
  .travel-orbit:hover .travel-orbit-controls button,.travel-orbit-controls:focus-within button { opacity: .88; }
  .travel-orbit-controls button:hover { opacity: 1; }
  .travel-orbit-controls .is-previous:hover svg,.travel-orbit-controls .is-previous:hover::before { transform: rotate(var(--orbit-control-angle)) translateX(-11px); }
  .travel-orbit-controls .is-next:hover svg,.travel-orbit-controls .is-next:hover::before { transform: rotate(var(--orbit-control-angle)) translateX(11px); }
}
@media (max-width:760px) {
  .travel-orbit-photo { height: min(350px, 52dvh); }
  .travel-orbit-controls button { width: 44px; }
  .travel-orbit-controls svg,.travel-orbit-controls button::before { width: 60px; height: 60px; }
}
@media (prefers-reduced-motion:reduce) { .travel-orbit-controls button,.travel-orbit-controls svg,.travel-orbit-controls button::before { transition: none; } }
</style>
